import instance from '../utils/axios';

export type ProductionOrderStatus =
  | 'DRAFT'
  | 'READY'
  | 'CUTTING'
  | 'STITCHING'
  | 'FINISHING'
  | 'COMPLETED'
  | 'ON_HOLD'
  | 'CANCELLED';

export interface MaterialIssue {
  id: number;
  production_order_id: number;
  production_order_bom_id: number;
  design_material_id: number | null;
  material_name: string;
  material_type: string;
  garment_part: string;
  uom: string;
  issued_qty: number;
  received_qty: number | null;
  discrepancy_notes: string | null;
  status: 'ISSUED' | 'RECEIVED' | 'DISCREPANCY';
  issued_by: string;
  issued_at: string;
  received_by: string | null;
  received_at: string | null;
  notes: string | null;
}

export interface ProductionOrder {
  id: number;
  order_number: string;
  design_no: string;
  company_id: number;
  status: ProductionOrderStatus;
  planned_qty: Record<string, Record<string, number>> | null;
  total_planned: number;
  total_cut: number;
  total_stitched: number;
  total_finished: number;
  target_date: string | null;
  cutting_start_date: string | null;
  stitching_start_date: string | null;
  finishing_start_date: string | null;
  completed_at: string | null;
  notes: string | null;
  priority: string;
  receiving_verified: boolean;
  receiving_verified_at: string | null;
  receiving_verified_by: string | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  design?: {
    id: number;
    design_no: string;
    name: string | null;
    product_type: string | null;
    image_url: string | null;
    approval_status?: string;
  };
  bom_items?: any[];
  cutting_records?: any[];
  worker_assignments?: any[];
  production_batches?: any[];
  receipts?: any[];
  material_issues?: MaterialIssue[];
  progress?: {
    total_planned: number;
    total_cut: number;
    total_stitched: number;
    total_finished: number;
  };
  _count?: {
    cutting_records: number;
    worker_assignments: number;
    production_batches: number;
  };
}

export interface CreateProductionOrderDTO {
  design_no: string;
  planned_qty?: Record<string, Record<string, number>>;
  target_date?: string;
  notes?: string;
  priority?: string;
}

export interface UpdateProductionOrderDTO {
  planned_qty?: Record<string, Record<string, number>>;
  target_date?: string;
  notes?: string;
  priority?: string;
  status?: ProductionOrderStatus;
}

export interface AddCuttingEntry {
  garment_part: string;
  received_date: string;
  fabric_meter: number;
  inner_fabric_meter?: number;
  xs_pieces?: number;
  s_pieces?: number;
  m_pieces?: number;
  l_pieces?: number;
  xl_pieces?: number;
  xxl_pieces?: number;
  three_xl_pieces?: number;
  cutting_date?: string;
  rate?: number;
  notes?: string;
}

export interface AssignWorkerItem {
  worker_id: number;
  garment_part: string;
  size: string;
  quantity: number;
}

export interface AssignWorkersData {
  assignments: AssignWorkerItem[];
  issue_date: string;
  stitching_rate?: number;
  notes?: string;
}

export interface CreateBatchData {
  garment_part: string;
  design_cutting_id?: number;
  total_issued: number;
  notes?: string;
}

export interface CuttingCapacity {
  [part: string]: {
    [size: string]: { cut: number; assigned: number; available: number };
  };
}

export interface IssueMaterialItem {
  bom_item_id: number;
  issued_qty: number;
  notes?: string;
}

export interface ReceiveMaterialItem {
  issue_id: number;
  received_qty: number;
  discrepancy_notes?: string;
}

export const productionOrdersApi = {
  getAll: async (params?: { status?: string; design_no?: string; priority?: string }): Promise<ProductionOrder[]> => {
    const response = await instance.get('/production-orders', { params });
    return response.data;
  },

  getById: async (id: number): Promise<ProductionOrder> => {
    const response = await instance.get(`/production-orders/${id}`);
    return response.data;
  },

  getByDesign: async (designNo: string): Promise<ProductionOrder[]> => {
    const response = await instance.get(`/production-orders/by-design/${designNo}`);
    return response.data;
  },

  create: async (data: CreateProductionOrderDTO): Promise<ProductionOrder> => {
    const response = await instance.post('/production-orders', data);
    return response.data;
  },

  update: async (id: number, data: UpdateProductionOrderDTO): Promise<ProductionOrder> => {
    const response = await instance.patch(`/production-orders/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await instance.delete(`/production-orders/${id}`);
  },

  refreshStatus: async (id: number): Promise<ProductionOrder> => {
    const response = await instance.post(`/production-orders/${id}/refresh-status`);
    return response.data;
  },

  issue: async (id: number): Promise<{ order: ProductionOrder; reservations: Array<{ bom_item_id: number; material_name: string; planned_qty: number; reserved_qty: number; status: 'RESERVED' | 'PARTIAL' | 'NOT_FOUND' }> }> => {
    const response = await instance.post(`/production-orders/${id}/issue`);
    return response.data;
  },

  issueMaterials: async (id: number, items: IssueMaterialItem[]): Promise<{
    message: string;
    issues: Array<{ bom_item_id: number; material_name: string; issued_qty: number; status: string }>;
    order: ProductionOrder;
  }> => {
    const response = await instance.post(`/production-orders/${id}/issue-materials`, { items });
    return response.data;
  },

  receiveMaterials: async (id: number, items: ReceiveMaterialItem[]): Promise<{
    message: string;
    results: Array<{ issue_id: number; material_name: string; issued_qty: number; received_qty: number; status: string }>;
    has_discrepancies: boolean;
    order: ProductionOrder;
  }> => {
    const response = await instance.post(`/production-orders/${id}/receive-materials`, { items });
    return response.data;
  },

  getMaterialAvailability: async (id: number): Promise<{
    availability: any[];
    summary: {
      total_bom_items: number;
      linked_count: number;
      unlinked_count: number;
      all_sufficient: boolean;
      all_materials_issued: boolean;
      all_materials_received: boolean;
    };
  }> => {
    const response = await instance.get(`/production-orders/${id}/material-availability`);
    return response.data;
  },

  linkBomToAllocation: async (id: number, bom_item_id: number, design_material_id: number): Promise<{ message: string }> => {
    const response = await instance.post(`/production-orders/${id}/link-material`, { bom_item_id, design_material_id });
    return response.data;
  },

  submitReceipt: async (id: number, receipts: Array<{ garment_part: string; size: string; received_qty: number }>): Promise<{
    message: string;
    receipts: any[];
    has_discrepancies: boolean;
  }> => {
    const response = await instance.post(`/production-orders/${id}/submit-receipt`, { receipts });
    return response.data;
  },

  confirmReceipt: async (id: number): Promise<{ message: string; order: ProductionOrder }> => {
    const response = await instance.post(`/production-orders/${id}/confirm-receipt`);
    return response.data;
  },

  addCutting: async (id: number, entries: AddCuttingEntry[]): Promise<{
    message: string;
    created: number;
    errors: { index: number; error: string }[];
    records: any[];
  }> => {
    const response = await instance.post(`/production-orders/${id}/add-cutting`, { entries });
    return response.data;
  },

  assignWorkers: async (id: number, data: AssignWorkersData): Promise<{
    message: string;
    created: number;
    errors: { index: number; error: string }[];
    assignments: any[];
  }> => {
    const response = await instance.post(`/production-orders/${id}/assign-workers`, data);
    return response.data;
  },

  createBatch: async (id: number, data: CreateBatchData): Promise<{
    message: string;
    batch: any;
  }> => {
    const response = await instance.post(`/production-orders/${id}/create-batch`, data);
    return response.data;
  },

  getCuttingCapacity: async (id: number): Promise<{ capacity: CuttingCapacity }> => {
    const response = await instance.get(`/production-orders/${id}/cutting-capacity`);
    return response.data;
  },
};
